import axios from "axios";
import css from "./index.module.css";
import SEO from "@components/seo";
import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import InputMask from "react-input-mask";
import { isMobile } from "react-device-detect";

export default function Authorization() {
  const [page, setPage] = useState(0);
  const [phone, setPhone] = useState("");
  const [callId, setCallId] = useState("");
  const [token, setToken] = useState("");
  const [inn, setInn] = useState("");
  const [loadingData, setLoadingData] = useState(false);
  const [orgInfo, setOrgInfo] = useState<any>(null);
  const lastRequestedInn = useRef<string>("");
  const innDebounceTimer = useRef<NodeJS.Timeout | null>(null);

  const normalizePhone = (phone: string): string => {
    // Убираем все символы кроме цифр
    const cleaned = phone.replace(/\D/g, "");
    // Если номер начинается с 7, заменяем на +7, иначе добавляем +7
    if (cleaned.startsWith("7")) {
      return "+7" + cleaned.slice(1);
    }
    return "+7" + cleaned;
  };

  const handleSubmit = (event: any) => {
    event.preventDefault();
    const formData = new FormData(event.target);

    // Нормализуем номер телефона перед отправкой
    const normalizedPhone = normalizePhone(phone);
    formData.set("phone", normalizedPhone);

    axios
      .post(`${import.meta.env.VITE_API_URL}/auth/phone`, formData, {
        headers: {
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        setCallId(response.data.id);
        setPage(1);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const handleSubmitRegistration = (event: any) => {
    event.preventDefault();

    if (!orgInfo) {
      return toast.error("Пожалуйста, найдите свою организацию");
    }

    const formData = new FormData(event.target);
    formData.append(
      "orgInfo",
      JSON.stringify({
        name: orgInfo.value,
        inn: orgInfo.data.inn,
        ogrn: orgInfo.data.ogrn,
        status: orgInfo.data.state.status,
        address: orgInfo.data.address.unrestricted_value,
      })
    );

    axios
      .post(`${import.meta.env.VITE_API_URL}/auth/registration`, formData, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      })
      .then((_) => {
        toast.success("Регистрация успешно выполнена");
        localStorage.setItem("authToken", token);
        window.location.replace("/webapp");
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    if (callId && page === 1) {
      const interval = setInterval(() => {
        const normalizedPhone = normalizePhone(phone);
        axios
          .get(
            `${
              import.meta.env.VITE_API_URL
            }/auth/phone/status?callId=${callId}&phone=${normalizedPhone}`,
            {
              headers: {
                "Content-Type": "application/json",
              },
            }
          )
          .then((response) => {
            if (response.data.code === 1) {
              toast.success("Номер подтвержден, спасибо");

              if (response.data.type == 0) {
                localStorage.setItem("authToken", response.data.token);
                window.location.replace("/webapp");
              } else {
                setPage(2);
                setToken(response.data.token);
              }
            }
          })
          .catch((error) => {
            setCallId("");
            setPage(0);
            toast.error("Ошибка при подтверждении номера");
            console.log(error);
          });
      }, 2000);

      return () => clearInterval(interval);
    }
  }, [callId, page, phone]);

  const handleCheck = () => {
    const normalizedPhone = normalizePhone(phone);
    axios
      .get(
        `${
          import.meta.env.VITE_API_URL
        }/auth/phone/status?callId=${callId}&phone=${normalizedPhone}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      )
      .then((response) => {
        if (response.data.code === 1) {
          toast.success("Номер подтвержден, спасибо");

          if (response.data.type == 0) {
            localStorage.setItem("authToken", response.data.token);
            window.location.replace("/webapp");
          } else {
            setPage(2);
            setToken(response.data.token);
          }
        } else {
          toast.info("Ожидаем проверки номера...");
        }
      })
      .catch((error) => {
        setCallId("");
        setPage(0);
        toast.error("Ошибка при подтверждении номера");
        console.log(error);
      });
  };

  useEffect(() => {
    if (innDebounceTimer.current) {
      clearTimeout(innDebounceTimer.current);
      innDebounceTimer.current = null;
    }

    const isInnComplete =
      (inn.length === 10 || inn.length === 12) && /^\d+$/.test(inn);

    if (!isInnComplete) {
      if (inn.length < 10) {
        setOrgInfo(null);
        lastRequestedInn.current = "";
        setLoadingData(false);
      }
      return;
    }

    const isNewInn = inn !== lastRequestedInn.current;

    if (isNewInn && page === 2 && token) {
      innDebounceTimer.current = setTimeout(() => {
        const currentInn = inn;
        const stillComplete =
          (currentInn.length === 10 || currentInn.length === 12) &&
          /^\d+$/.test(currentInn);
        const stillNew = currentInn !== lastRequestedInn.current;

        if (stillComplete && stillNew && page === 2 && token) {
          lastRequestedInn.current = currentInn;
          setLoadingData(true);
          setOrgInfo(null);

          axios
            .get(
              `${
                import.meta.env.VITE_API_URL
              }/dadata/findOrg?query=${currentInn}`,
              {
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
              }
            )
            .then((response) => {
              if (response.data.status == "Error") {
                setLoadingData(false);
                lastRequestedInn.current = "";
                toast.error("Ошибка при поиске организации");
                return;
              }

              setOrgInfo(response.data);
              setLoadingData(false);
            })
            .catch((error) => {
              setLoadingData(false);
              lastRequestedInn.current = "";
              console.log(error);
            });
        }
      }, 800);
    }

    return () => {
      if (innDebounceTimer.current) {
        clearTimeout(innDebounceTimer.current);
      }
    };
  }, [inn, page, token]);

  return (
    <div className={css.container}>
      <SEO
        title="Авторизация"
        description="Авторизация"
        ogUrl="https://рыбныимир.рф"
      />

      <div className={css.component}>
        <div className={css.logoContainer}>
          <img src="/assets/images/icon.png" alt="logo" className={css.logo} />
          <h1>Рыбный мир</h1>
        </div>

        {page === 0 && (
          <form className={css.form} onSubmit={handleSubmit}>
            <a className={css.head}>👋 Добро пожаловать!</a>

            <InputMask
              mask="+7 (999) 999-99-99"
              type="tel"
              placeholder="Введите номер телефона"
              name="phone"
              value={phone}
              onChange={(e: any) => setPhone(e.target.value)}
              required
            />

            <button type="submit">Продолжить</button>
            <a className={css.foot}>
              Нажимая кнопку “продолжить”, я соглашаюсь с политикой обработки
              персональных данных
            </a>
          </form>
        )}

        {page === 1 && (
          <form className={css.form}>
            <a className={css.head}>Выполните звонок на номер</a>
            <a className={css.telephone} href="tel:+78005558607">
              <i className="fi fi-rr-circle-phone"></i> +7 800 555-86-07
            </a>
            {isMobile && (
              <button type="button" onClick={handleCheck}>
                Проверить
              </button>
            )}
            {!isMobile && (
              <img
                src="/qr.png"
                alt="qr"
                className={css.qrCode}
                style={{ width: "320px", height: "320px" }}
              />
            )}
            <a className={css.foot}>
              Выполняя звонок, я соглашаюсь с политикой обработки персональных
              данных
            </a>
          </form>
        )}

        {page === 2 && (
          <form className={css.form} onSubmit={handleSubmitRegistration}>
            <a className={css.head}>Регистрация профиля</a>

            <input
              placeholder="Желаемый логин"
              type="text"
              name="username"
              required
            />
            <input
              placeholder="Как вас зовут?"
              type="text"
              name="firstName"
              required
            />
            <input
              placeholder="ИНН вашей организации"
              type="text"
              name="inn"
              value={inn}
              onChange={(e: any) => {
                const val = e.target.value.replace(/\D/g, "").slice(0, 12);
                setInn(val);
              }}
              pattern="\d{10,12}"
              minLength={10}
              maxLength={12}
              required
              inputMode="numeric"
            />

            {loadingData && (
              <div className={css.loadingInfo}>
                <b>Ищем вашу организацию...</b>
                <p>Подождите, пожалуйста...</p>
              </div>
            )}

            {!loadingData && orgInfo && (
              <div className={css.orgInfo}>
                <div className={css.infoItem}>
                  <b>{orgInfo.value}</b>
                  <p>{orgInfo.data.address.unrestricted_value}</p>
                </div>

                <div
                  className={`${css.statusInfo} ${
                    orgInfo.data.state.status === "ACTIVE"
                      ? css.active
                      : css.inactive
                  }`}
                >
                  <span>
                    {orgInfo.data.state.status === "ACTIVE"
                      ? "Действующая"
                      : "Ликвидирована"}
                  </span>
                </div>
              </div>
            )}

            <button type="submit">Продолжить</button>
            <a className={css.foot}>
              Нажимая кнопку “продолжить”, я соглашаюсь с политикой обработки
              персональных данных
            </a>
          </form>
        )}

        <a>v1.4</a>
      </div>
    </div>
  );
}
